import React, { useState, useEffect } from 'react';
import { 
    BarChart3, 
    Globe, 
    Code,
    CheckCircle, 
    AlertCircle, 
    Loader, 
    Eye,
    EyeOff,
    Save,
    RefreshCw
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const AnalyticsSettings = () => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [showScript, setShowScript] = useState(false);
    
    const [settings, setSettings] = useState({
        google_analytics_enabled: { value: false },
        google_analytics_id: { value: '' },
        google_analytics_head_script: { value: '' },
        google_analytics_body_script: { value: '' },
        google_analytics_custom_head: { value: '' },
        google_analytics_custom_body: { value: '' }
    });
    
    const [formData, setFormData] = useState({
        enabled: false,
        measurementId: '',
        customHeadScript: '',
        customBodyScript: ''
    });

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await api.get('/admin/settings/google-analytics');
            
            if (response.data.success) {
                const data = response.data.data;
                setSettings(data);
                
                // Update form data
                setFormData({
                    enabled: data.google_analytics_enabled?.value || false,
                    measurementId: data.google_analytics_id?.value || '',
                    customHeadScript: data.google_analytics_custom_head?.value || '',
                    customBodyScript: data.google_analytics_custom_body?.value || ''
                });
            } else {
                setError('Failed to load analytics settings');
            }
        } catch (err) {
            console.error('Error fetching analytics settings:', err);
            setError('Unable to load analytics settings');
        } finally {
            setLoading(false);
        }
    };

    const generateDefaultScripts = (measurementId) => {
        if (!measurementId) return { head: '', body: '' };
        
        const headScript = `<!-- Google tag (gtag.js) -->
<script async src="https://www.googletagmanager.com/gtag/js?id=${measurementId}"></script>
<script>
  window.dataLayer = window.dataLayer || [];
  function gtag(){dataLayer.push(arguments);}
  gtag('js', new Date());
  gtag('config', '${measurementId}');
</script>`;

        const bodyScript = `<!-- Google Analytics (noscript) -->
<noscript>
  <iframe src="https://www.googletagmanager.com/ns.html?id=${measurementId}"
          height="0" width="0" style="display:none;visibility:hidden"></iframe>
</noscript>`;
        
        return { head: headScript, body: bodyScript };
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            setError(null);
            setSuccess(null);

            // Validate inputs
            if (formData.enabled && !formData.measurementId && !formData.customHeadScript) {
                setError('Please provide either a Measurement ID or custom head script');
                return;
            }

            if (formData.measurementId && !/^G-[A-Z0-9]+$/.test(formData.measurementId)) {
                setError('Invalid Google Analytics Measurement ID format (should be like G-XXXXXXXXXX)');
                return;
            }

            const payload = {
                enabled: formData.enabled,
                measurementId: formData.measurementId,
                customHeadScript: formData.customHeadScript,
                customBodyScript: formData.customBodyScript
            };

            const response = await api.put('/admin/settings/google-analytics', payload);
            
            if (response.data.success) {
                setSuccess('Analytics settings updated successfully');
                await fetchSettings(); // Refresh the data
            } else {
                setError(response.data.message || 'Failed to update settings');
            }
        } catch (err) {
            console.error('Error saving analytics settings:', err);
            setError(err.response?.data?.message || 'Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        
        // Note: Auto-generation removed since we now combine auto + custom scripts
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-900 pt-20 pb-12">
                <div className="max-w-4xl mx-auto px-6">
                    <div className="glass-card p-8 text-center">
                        <Loader className="w-12 h-12 text-purple-500 mx-auto mb-3 animate-spin" />
                        <p className="text-gray-400">Loading analytics settings...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-900 pt-20 pb-12">
            <div className="max-w-4xl mx-auto px-6">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center gap-3 mb-2">
                        <BarChart3 className="w-8 h-8 text-green-400" />
                        <h1 className="text-4xl font-black gradient-text">Google Analytics Settings</h1>
                    </div>
                    <p className="text-gray-400">
                        Configure Google Analytics tracking for The Deep Wave platform
                    </p>
                </div>

                {/* Error/Success Messages */}
                {error && (
                    <div className="glass-card p-4 mb-6 border border-red-500/30 bg-red-500/10">
                        <div className="flex items-center gap-2">
                            <AlertCircle className="w-5 h-5 text-red-400" />
                            <span className="text-red-400">{error}</span>
                        </div>
                    </div>
                )}

                {success && (
                    <div className="glass-card p-4 mb-6 border border-green-500/30 bg-green-500/10">
                        <div className="flex items-center gap-2">
                            <CheckCircle className="w-5 h-5 text-green-400" />
                            <span className="text-green-400">{success}</span>
                        </div>
                    </div>
                )}

                {/* Main Settings Form */}
                <div className="glass-card p-8">
                    <div className="space-y-8">
                        {/* Enable/Disable Toggle */}
                        <div>
                            <label className="flex items-center gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={formData.enabled}
                                    onChange={(e) => handleInputChange('enabled', e.target.checked)}
                                    className="w-5 h-5 rounded bg-gray-700 border-gray-600 text-green-500 focus:ring-green-500 focus:ring-offset-gray-900"
                                />
                                <span className="text-lg font-semibold text-white">
                                    Enable Google Analytics Tracking
                                </span>
                            </label>
                            <p className="text-gray-400 text-sm mt-2 ml-8">
                                When enabled, Google Analytics tracking code will be injected into all pages
                            </p>
                        </div>

                        {/* Measurement ID */}
                        {formData.enabled && (
                            <>
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        Google Analytics Measurement ID (Optional)
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.measurementId}
                                        onChange={(e) => handleInputChange('measurementId', e.target.value)}
                                        placeholder="G-XXXXXXXXXX"
                                        className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    />
                                    <p className="text-gray-400 text-sm mt-2">
                                        If provided, standard Google Analytics tracking code will be auto-generated and combined with any custom scripts below
                                    </p>
                                </div>

                                {/* Info Box */}
                                <div className="bg-blue-600/20 border border-blue-500/30 p-4 rounded-lg">
                                    <div className="flex items-start gap-3">
                                        <BarChart3 className="w-5 h-5 text-blue-400 mt-0.5" />
                                        <div>
                                            <h4 className="text-blue-300 font-semibold mb-1">Combined Approach</h4>
                                            <p className="text-blue-100 text-sm">
                                                You can use both a Measurement ID AND custom scripts. The system will:
                                            </p>
                                            <ul className="text-blue-100 text-sm mt-2 space-y-1">
                                                <li>• Generate standard GA tracking code from your Measurement ID</li>
                                                <li>• Append any custom scripts you add below</li>
                                                <li>• Inject the combined scripts into your pages</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>

                                {/* Script Editors */}
                                <div className="space-y-6">
                                    {/* Head Script */}
                                    <div>
                                        <div className="flex items-center justify-between mb-2">
                                            <label className="block text-sm font-medium text-gray-300">
                                                <span className="flex items-center gap-2">
                                                    <Code className="w-4 h-4" />
                                                    Additional Head Scripts (Optional)
                                                </span>
                                            </label>
                                            <button
                                                onClick={() => setShowScript(!showScript)}
                                                className="flex items-center gap-2 text-purple-400 hover:text-purple-300"
                                            >
                                                {showScript ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                                {showScript ? 'Hide' : 'Show'} Custom Scripts
                                            </button>
                                        </div>
                                        
                                        {showScript && (
                                            <div className="relative">
                                                <textarea
                                                    value={formData.customHeadScript}
                                                    onChange={(e) => handleInputChange('customHeadScript', e.target.value)}
                                                    placeholder="Add additional head scripts here (GTM, Facebook Pixel, custom tracking, etc.)..."
                                                    rows={8}
                                                    className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono text-sm"
                                                />
                                                <div className="absolute top-3 right-3 bg-purple-600 text-white px-2 py-1 rounded text-xs">
                                                    ADDITIONAL HEAD
                                                </div>
                                            </div>
                                        )}
                                        
                                        <p className="text-gray-400 text-sm mt-2">
                                            Additional scripts to append after the auto-generated GA code in &lt;head&gt;
                                        </p>
                                    </div>

                                    {/* Body Script */}
                                    <div>
                                        <div className="flex items-center justify-between mb-2">
                                            <label className="block text-sm font-medium text-gray-300">
                                                <span className="flex items-center gap-2">
                                                    <Code className="w-4 h-4" />
                                                    Additional Body Scripts (Optional)
                                                </span>
                                            </label>
                                        </div>
                                        
                                        {showScript && (
                                            <div className="relative">
                                                <textarea
                                                    value={formData.customBodyScript}
                                                    onChange={(e) => handleInputChange('customBodyScript', e.target.value)}
                                                    placeholder="Add additional body scripts here (GTM noscript, Facebook Pixel noscript, etc.)..."
                                                    rows={6}
                                                    className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono text-sm"
                                                />
                                                <div className="absolute top-3 right-3 bg-blue-600 text-white px-2 py-1 rounded text-xs">
                                                    ADDITIONAL BODY
                                                </div>
                                            </div>
                                        )}
                                        
                                        <p className="text-gray-400 text-sm mt-2">
                                            Additional scripts to append after the auto-generated GA noscript in &lt;body&gt;
                                        </p>
                                    </div>
                                </div>

                                {/* Combined Script Preview */}
                                {(formData.measurementId || formData.customHeadScript || formData.customBodyScript) && (
                                    <div className="border-t border-gray-700 pt-6">
                                        <h4 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                                            <Eye className="w-5 h-5 text-green-400" />
                                            Final Combined Scripts Preview
                                        </h4>
                                        
                                        <div className="space-y-4">
                                            {/* Head Preview */}
                                            {(formData.measurementId || formData.customHeadScript) && (
                                                <div className="bg-gray-800/50 p-4 rounded-lg">
                                                    <h5 className="text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
                                                        <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                                                        HEAD Section (combined)
                                                    </h5>
                                                    <div className="bg-gray-900 p-3 rounded font-mono text-xs text-gray-300 max-h-32 overflow-y-auto">
                                                        {formData.measurementId && (
                                                            <div className="text-green-400">
                                                                {generateDefaultScripts(formData.measurementId).head}
                                                            </div>
                                                        )}
                                                        {formData.measurementId && formData.customHeadScript && (
                                                            <div className="my-2 border-t border-gray-700"></div>
                                                        )}
                                                        {formData.customHeadScript && (
                                                            <div className="text-blue-400">
                                                                {formData.customHeadScript}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Body Preview */}
                                            {(formData.measurementId || formData.customBodyScript) && (
                                                <div className="bg-gray-800/50 p-4 rounded-lg">
                                                    <h5 className="text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
                                                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                                        BODY Section (combined)
                                                    </h5>
                                                    <div className="bg-gray-900 p-3 rounded font-mono text-xs text-gray-300 max-h-32 overflow-y-auto">
                                                        {formData.measurementId && (
                                                            <div className="text-green-400">
                                                                {generateDefaultScripts(formData.measurementId).body}
                                                            </div>
                                                        )}
                                                        {formData.measurementId && formData.customBodyScript && (
                                                            <div className="my-2 border-t border-gray-700"></div>
                                                        )}
                                                        {formData.customBodyScript && (
                                                            <div className="text-blue-400">
                                                                {formData.customBodyScript}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}

                        {/* Current Status */}
                        <div className="border-t border-gray-700 pt-6">
                            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                                <Globe className="w-5 h-5 text-blue-400" />
                                Current Status
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="bg-gray-800/50 p-4 rounded-lg">
                                    <div className="flex items-center gap-2 mb-2">
                                        {settings.google_analytics_enabled?.value ? (
                                            <CheckCircle className="w-5 h-5 text-green-400" />
                                        ) : (
                                            <AlertCircle className="w-5 h-5 text-yellow-400" />
                                        )}
                                        <span className="font-medium text-white">Tracking Status</span>
                                    </div>
                                    <p className="text-sm text-gray-400">
                                        {settings.google_analytics_enabled?.value ? 'Enabled' : 'Disabled'}
                                    </p>
                                </div>
                                <div className="bg-gray-800/50 p-4 rounded-lg">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Code className="w-5 h-5 text-purple-400" />
                                        <span className="font-medium text-white">Script Status</span>
                                    </div>
                                    <p className="text-sm text-gray-400">
                                        Head: {settings.google_analytics_head_script?.value ? 'Configured' : 'Not configured'}
                                        <br />
                                        Body: {settings.google_analytics_body_script?.value ? 'Configured' : 'Not configured'}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Save Button */}
                        <div className="flex gap-4">
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                className="flex items-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white font-semibold rounded-lg transition-colors"
                            >
                                {saving ? (
                                    <Loader className="w-5 h-5 animate-spin" />
                                ) : (
                                    <Save className="w-5 h-5" />
                                )}
                                {saving ? 'Saving...' : 'Save Settings'}
                            </button>
                            
                            <button
                                onClick={fetchSettings}
                                disabled={loading || saving}
                                className="flex items-center gap-2 px-6 py-3 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 text-white font-semibold rounded-lg transition-colors"
                            >
                                <RefreshCw className="w-5 h-5" />
                                Refresh
                            </button>
                        </div>
                    </div>
                </div>

                {/* Help Section */}
                <div className="glass-card p-6 mt-6 border border-blue-500/30">
                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-blue-400" />
                        How to Get Your Google Analytics ID
                    </h3>
                    <div className="space-y-3 text-gray-300">
                        <p><strong>1.</strong> Go to your Google Analytics account</p>
                        <p><strong>2.</strong> Select your website property</p>
                        <p><strong>3.</strong> Go to Admin → Data Streams → Web</p>
                        <p><strong>4.</strong> Copy your Measurement ID (starts with "G-")</p>
                        <p><strong>5.</strong> Paste it in the field above</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AnalyticsSettings;